import React from 'react';
import { ForeignActivitiesRealEstateItemModel } from 'forms/sections/foreign/activities/realEstate/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonRequired, CommonScrollIntoView
} from 'types/props';
import ValidationElement from 'components/Form/ValidationElement';
import { CheckboxModel } from 'forms/shared/types';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import NotApplicable from 'components/Form/NotApplicable';
import Currency from 'components/Form/Currency';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import CoOwners from '../Shared/CoOwners';

interface Props extends Partial<ForeignActivitiesRealEstateItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView

  prefix: string
}

export default class RealEstateInterest extends ValidationElement<Props> {
  update = (queue: Partial<ForeignActivitiesRealEstateItemModel>) => {
    this.props.onUpdate({
      InterestTypes: this.props.InterestTypes,
      RealEstateType: this.props.RealEstateType,
      Address: this.props.Address,
      Acquired: this.props.Acquired,
      HowAcquired: this.props.HowAcquired,
      Cost: this.props.Cost,
      CostEstimated: this.props.CostEstimated,
      Sold: this.props.Sold,
      SoldNotApplicable: this.props.SoldNotApplicable,
      CoOwners: this.props.CoOwners,
      ...queue,
    });
  };

  updateInterestTypes = (values: CheckboxModel) => {
    const interestType = values.value;
    const selected = [...((this.props.InterestTypes || {}).values || [])];
    if (selected.includes(interestType)) {
      selected.splice(selected.indexOf(interestType), 1);
    } else {
      selected.push(interestType);
    }

    this.update({
      InterestTypes: { values: selected },
    });
  };

  updateRealEstateType = (values: ForeignActivitiesRealEstateItemModel['RealEstateType']) => {
    this.update({
      RealEstateType: values,
    });
  };

  updateAddress = (values: ForeignActivitiesRealEstateItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateAcquired = (values: ForeignActivitiesRealEstateItemModel['Acquired']) => {
    this.update({
      Acquired: values,
    });
  };

  updateHowAcquired = (values: ForeignActivitiesRealEstateItemModel['HowAcquired']) => {
    this.update({
      HowAcquired: values,
    });
  };

  updateCost = (values: ForeignActivitiesRealEstateItemModel['Cost']) => {
    this.update({
      Cost: values,
    });
  };

  updateCostEstimated = (values: ForeignActivitiesRealEstateItemModel['CostEstimated']) => {
    this.update({
      CostEstimated: values,
    });
  };

  updateSold = (values: ForeignActivitiesRealEstateItemModel['Sold']) => {
    this.update({
      Sold: values,
    });
  };

  updateSoldNotApplicable = (values: ForeignActivitiesRealEstateItemModel['SoldNotApplicable']) => {
    this.update({
      Sold: values.applicable ? this.props.Sold : {},
      SoldNotApplicable: values,
    });
  };

  updateCoOwners = (values: ForeignActivitiesRealEstateItemModel['CoOwners']) => {
    this.update({
      CoOwners: values,
    });
  };

  render() {
    const { prefix, Sold } = this.props;
    const attribute = `List.items[${this.props.$index}].Item`;
    const includeNAConfirmation = Boolean(Sold?.day || Sold?.month || Sold?.year);
    return (
      <div className="interest">
        <Field
          title={i18n.t('foreign.activities.realestate.interest.heading.interestTypes')}
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.InterestTypes`}
        >
          {i18n.m('foreign.activities.realestate.interest.para.checkAll')}
          <CheckboxGroup
            className="interest-types option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValues={(this.props.InterestTypes || {}).values}
            attribute={`${attribute}.InterestTypes`}
          >
            <Checkbox
              name="interest-type"
              label={i18n.t('foreign.activities.realestate.interest.label.interestTypes.yourself')}
              value="Yourself"
              className="yourself"
              onError={this.props.onError}
              onUpdate={this.updateInterestTypes}
            />
            <Checkbox
              name="interest-type"
              label={i18n.t('foreign.activities.realestate.interest.label.interestTypes.spouse')}
              value="Spouse"
              className="spouse"
              onError={this.props.onError}
              onUpdate={this.updateInterestTypes}
            />
            <Checkbox
              name="interest-type"
              label={i18n.t(
                'foreign.activities.realestate.interest.label.interestTypes.cohabitant'
              )}
              value="Cohabitant"
              className="cohabitant"
              onError={this.props.onError}
              onUpdate={this.updateInterestTypes}
            />
            <Checkbox
              name="interest-type"
              label={i18n.t(
                'foreign.activities.realestate.interest.label.interestTypes.dependentChildren'
              )}
              value="DependentChildren"
              className="dependent-children"
              onError={this.props.onError}
              onUpdate={this.updateInterestTypes}
            />
          </CheckboxGroup>
        </Field>

        <Field
          title={i18n.t('foreign.activities.realestate.interest.heading.realEstateType')}
          adjustFor="p"
          attribute={`${attribute}.RealEstateType`}
          scrollIntoView={this.props.scrollIntoView}
        >
          {i18n.t('foreign.activities.realestate.interest.para.realEstateType')}
          <Text
            name="RealEstateType"
            className="realestate-type"
            {...this.props.RealEstateType}
            onUpdate={this.updateRealEstateType}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.RealEstateType`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.realestate.interest.heading.address')}
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Address`}
        >
          <Location
            name="Address"
            {...this.props.Address}
            layout={Location.STREET_CITY_COUNTRY}
            fields={['street', 'city', 'country']}
            label={i18n.t('foreign.activities.realestate.interest.label.address')}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Address`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.realestate.interest.heading.acquired')}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Acquired`}
        >
          <DateControl
            name="Acquired"
            className="acquired"
            {...this.props.Acquired}
            minDateEqualTo
            noMaxDate
            onUpdate={this.updateAcquired}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Acquired`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.realestate.interest.heading.howAcquired')}
          adjustFor="p"
          attribute={`${attribute}.HowAcquired`}
          scrollIntoView={this.props.scrollIntoView}
        >
          {i18n.t('foreign.activities.realestate.interest.para.howAcquired')}
          <Textarea
            name="HowAcquired"
            className="how-acquired"
            {...this.props.HowAcquired}
            onUpdate={this.updateHowAcquired}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.HowAcquired`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.realestate.interest.heading.sold')}
          help="foreign.activities.realestate.interest.help.sold"
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Sold`}
        >
          <NotApplicable
            name="SoldNotApplicable"
            {...this.props.SoldNotApplicable}
            label={i18n.t('foreign.activities.realestate.interest.label.soldNotApplicable')}
            or={i18n.m('foreign.activities.realestate.interest.label.or')}
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateSoldNotApplicable}
            attribute={`${attribute}.SoldNotApplicable`}
            includeNAConfirmation={includeNAConfirmation}
          >
            <DateControl
              name="Sold"
              className="sold"
              {...this.props.Sold}
              onUpdate={this.updateSold}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Sold`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('foreign.activities.realestate.interest.heading.cost')}
          help="foreign.activities.realestate.interest.help.cost"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Cost`}
        >
          <Currency
            name="Cost"
            className="cost"
            {...this.props.Cost}
            min="0"
            onUpdate={this.updateCost}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Cost`}
          />
          <div className="flags">
            <Checkbox
              name="CostEstimated"
              label={i18n.t('foreign.activities.realestate.interest.label.costEstimated')}
              toggle="false"
              {...this.props.CostEstimated}
              onUpdate={this.updateCostEstimated}
              onError={this.props.onError}
              attribute={`${attribute}.CostEstimated`}
            />
          </div>
        </Field>

        <CoOwners
          prefix={prefix}
          {...this.props.CoOwners}
          onUpdate={this.updateCoOwners}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CoOwners`}
          peopleWhoKnowYou={this.props.peopleWhoKnowYou}
        />
      </div>
    );
  }
}

RealEstateInterest.defaultProps = {
  SoldNotApplicable: { applicable: true },
  prefix: 'activities.realestate.interest',
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode) => arr,
};
